import CloseIcon from "@mui/icons-material/Close";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { t } from "i18next";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import AppLoader from "../../../components/AppLoader";
import GlobalButton from "../../../components/CustomButton";
import Label from "../../../components/Label";
import RomanianPhoneInput from "../../../components/romaniaPhoneInput";
import TextInput from "../../../components/TextInputs";
import appcolors from "../../../constants/Colors";
import { handleOpenSnackBar } from "../../../utils";
import { registerUserValidation } from "../../../utils/Validation";
import {
  createCompanyCustomer,
  updateCompanyCustomer,
} from "../../../services/company/companyCustomersApiFn";
import UploadImageAvatar from "../../../components/UploadImage";
import axios from "axios";
import { baseUrl } from "../../../services";
import { useSelector } from "react-redux";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      margin: 12,
    },
  },
}));

let paramsData = {};

const AddEditCustomer = forwardRef(({}, ref) => {
  const userData = useSelector((state) => state.userData.user);
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signupAs, setSignupAs] = useState("individual");
  const [isCuiLoading, setIsCuiLoading] = useState(false);
  const [inputValues, setInputValues] = useState({
    image: null,
    fname: "",
    lname: "",
    email: "",
    contact: "",
    password: "",
    cui: "",
  });

  const [inputError, setInputError] = useState({
    image: false,
    fname: false,
    lname: false,
    email: false,
    isValidEmail: false,
    contact: false,
    password: false,
    isStrongPassword: false,
    cui: false,
    isValidCui: false,
    isCuiExists: false,
  });

  useImperativeHandle(ref, () => ({
    isVisible(params) {
      setOpen(true);
      paramsData = params;
      if (params?.type === "edit") {
        setSignupAs(params?.data?.signupAs);
        setInputValues({
          ...inputValues,
          fname: params?.data?.firstname,
          lname: params?.data?.lastname,
          email: params?.data?.email,
          contact: params?.data?.contact,
          image: params?.data?.profileImg,
          ...(signupAs === "business" && { cui: params?.data?.cui }),
          password: "asda",
        });
      }
    },
  }));

  const handleClose = (callApi) => {
    setOpen(false);
    setInputValues({});
    setInputError({});
  };

  const handleImageChange = async (event) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInputValues((prevValues) => ({
        ...prevValues,
        image: response?.data?.url,
      }));
      setInputError((prevError) => ({
        ...prevError,
        image: false,
      }));
      handleOpenSnackBar("success", t("imageUploadSuccess"));
    } catch (error) {
      handleOpenSnackBar("error", t("imageUploadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeInputs = (e) => {
    const { name, value } = e.target;

    setInputError((pre) => ({
      ...pre,
      [name]: false,
      isValidEmail: name === "email" ? false : inputError.isValidEmail,
    }));

    setInputValues((pre) => ({
      ...pre,
      [name]: value,
    }));
  };

  const handleAddEditCustomer = async () => {
    try {
      const validate = registerUserValidation(
        inputValues,
        setInputError,
        signupAs
      );
      if (validate) return;

      const payload = {
        signupAs,
        firstname: inputValues.fname,
        lastname: signupAs === "individual" ? inputValues.lname : "",
        ...(signupAs === "business" && { cui: inputValues.cui }),
        email: inputValues.email,
        contact: inputValues.contact,
        profileImg: inputValues.image,

        ...(paramsData?.type == "add" && {
          createdBy: userData?._id,
          role: "User",
          password: inputValues.password,
        }),
      };

      setIsLoading(true);

      const response =
        paramsData?.type === "add"
          ? await createCompanyCustomer(payload)
          : await updateCompanyCustomer(
              userData?._id,
              paramsData?.data?._id,
              payload
            );
      if (response.data.status === "success") {
        handleClose();
        if (paramsData?.fetchAllCustomers) paramsData?.fetchAllCustomers();
        handleOpenSnackBar("success", response?.data?.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      // scroll="body"
    >
      <AppLoader isVisible={isLoading} />
      <IconButton
        onClick={handleClose}
        sx={{ position: "absolute", top: 5, right: 5 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle id="customized-dialog-title" sx={{ p: 2 }}>
        {paramsData.type === "add" ? t("addCustomer") : t("editCustomer")}
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12}>
            <Stack alignItems={"center"}>
              <UploadImageAvatar
                error={inputError.image}
                image={inputValues.image}
                handleImageChange={handleImageChange}
              />
              {inputError.image && (
                <FormHelperText sx={{ color: "custom.errorRed" }}>
                  {t("imageRequired")}
                </FormHelperText>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <RadioGroup
              row
              value={signupAs}
              onChange={(e) => {
                setSignupAs(e.target.value);
                setInputError((prev) => ({
                  ...prev,
                  fname: false,
                  lname: false,
                  email: false,
                  isValidEmail: false,
                  contact: false,
                  password: false,
                  isStrongPassword: false,
                  cui: false,
                  isValidCui: false,
                  isCuiExists: false,
                }));
              }}
              sx={{
                "& .MuiFormControlLabel-root": {
                  marginRight: 3,
                },
              }}
            >
              <FormControlLabel
                value="individual"
                control={
                  <Radio
                    sx={{
                      color: "primary.main",
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    color={
                      signupAs === "individual"
                        ? "primary.main"
                        : "text.secondary"
                    }
                    fontWeight={signupAs === "individual" ? 600 : 400}
                  >
                    {t("individual")}
                  </Typography>
                }
              />
              <FormControlLabel
                value="business"
                control={
                  <Radio
                    sx={{
                      color: "primary.main",
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    color={
                      signupAs === "business"
                        ? "primary.main"
                        : "text.secondary"
                    }
                    fontWeight={signupAs === "business" ? 600 : 400}
                  >
                    {t("business")}
                  </Typography>
                }
              />
            </RadioGroup>
          </Grid>

          <Grid item xs={12} sm={signupAs === "individual" ? 6 : 12}>
            <Label
              labelText={
                signupAs === "individual" ? t("firstName") : t("businessName")
              }
              required
              htmlFor="user-fname"
            />
            <TextInput
              id={"user-fname"}
              required
              inputError={inputError.fname}
              helperText={
                inputError.fname &&
                (signupAs === "individual"
                  ? t("firstNameRequire")
                  : t("businessNameRequired"))
              }
              placeholder={`${t("enter")} ${
                signupAs === "individual" ? t("firstName") : t("businessName")
              }`}
              type="text"
              name={"fname"}
              value={inputValues.fname}
              onChange={handleChangeInputs}
            />
          </Grid>

          {signupAs === "individual" && (
            <Grid item xs={12} sm={6}>
              <Label labelText={t("lastName")} required htmlFor="user-lname" />
              <TextInput
                id={"user-lname"}
                required
                inputError={inputError.lname}
                helperText={inputError.lname && t("lastNameRequire")}
                placeholder={`${t("enter")} ${t("lastName")}`}
                type="text"
                name={"lname"}
                value={inputValues.lname}
                onChange={handleChangeInputs}
              />
            </Grid>
          )}
          {signupAs === "business" && (
            <Grid item xs={12}>
              <Label
                labelText={`${t("cui")} (${t("startsWithRO")})`}
                required
                htmlFor="user-cui"
              />
              <TextInput
                id={"user-cui"}
                required
                inputError={
                  inputError.cui ||
                  inputError.isValidCui ||
                  inputError.isCuiExists
                }
                helperText={
                  inputError.cui
                    ? t("cuiRequired")
                    : inputError.isValidCui
                    ? t("invalidCuiFormat")
                    : inputError.isCuiExists
                    ? t("cuiAlreadyExists")
                    : null
                }
                placeholder={`${t("enter")} ${t("cui")}`}
                type="text"
                name={"cui"}
                value={inputValues.cui}
                onChange={handleChangeInputs}
                InputEndIcon={
                  isCuiLoading ? <CircularProgress size={20} /> : null
                }
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Label required labelText={t("email")} htmlFor={"email"} />
            <TextInput
              required
              inputError={inputError.email || inputError.isValidEmail}
              helperText={
                inputError.email
                  ? t("emailRequired")
                  : inputError.isValidEmail
                  ? t("invalidEmailFormat")
                  : null
              }
              id={"email"}
              placeholder={`${t("enter")} ${t("email")}`}
              name={"email"}
              value={inputValues.email}
              onChange={handleChangeInputs}
            />
          </Grid>

          <Grid item xs={12}>
            <Label
              labelText={t("contactNumber")}
              htmlFor={"contact_number"}
              required
            />
            <RomanianPhoneInput
              inputError={inputError.contact}
              value={inputValues.contact}
              onChange={(value) => {
                setInputValues((prev) => ({ ...prev, contact: value }));
                setInputError((err) => ({ ...err, contact: false }));
              }}
            />
          </Grid>

          {paramsData?.type === "add" && (
            <Grid item xs={12}>
              <Label required labelText={t("password")} htmlFor={"password"} />
              <TextInput
                required
                inputError={inputError.password}
                helperText={inputError.password ? t("passwordRequired") : null}
                placeholder={`${t("enter")} ${t("password")}`}
                type={showPassword ? "text" : "password"}
                name={"password"}
                value={inputValues.password}
                onChange={handleChangeInputs}
                InputEndIcon={
                  showPassword ? (
                    <Typography
                      variant="body2"
                      color={appcolors.black}
                      onClick={() => setShowPassword(false)}
                    >
                      {t("hide")}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      color={appcolors.black}
                      onClick={() => setShowPassword(true)}
                    >
                      {t("show")}
                    </Typography>
                  )
                }
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <GlobalButton
          variant={"outlined"}
          btnText={"cancel"}
          styleProps={{ width: 100, height: 40 }}
          handleClickBtn={handleClose}
        />
        <GlobalButton
          variant={"contained"}
          btnText={paramsData.type === "add" ? "addCustomer" : "editCustomer"}
          styleProps={{ width: 150, height: 40 }}
          handleClickBtn={handleAddEditCustomer}
        />
      </DialogActions>
    </BootstrapDialog>
  );
});

export default AddEditCustomer;
